import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../assets/styles/Workflow.css';

export interface ColumnFilterOption {
  value: string;
  label: string;
}

interface ColumnFilterDropdownProps {
  label: string;
  options: ColumnFilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
}

const PANEL_WIDTH = 220;

// Excel-style column-header filter: a small funnel button that opens a
// checkbox list of every distinct value for this column (fetched from the
// full dataset, not just what's currently on screen - see FDCPage.tsx). The
// selection is controlled by the parent, which re-fetches the table server-
// side whenever it changes - this component only renders the popover.
//
// The panel is rendered through a portal into document.body and positioned
// with `position: fixed` from the button's own screen coordinates - this
// page has several nested scrolling containers (the table wrapper, the page
// body), so a panel positioned normally (absolute, inside the table) can
// render past the visible area with no reliable way to measure "is there
// really room below" against every ancestor. Escaping to the body sidesteps
// that entirely: only the true viewport matters once positioning is fixed.
const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({ label, options, selected, onChange, searchable }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>(selected);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  // Position (and flip up/down) using the button's real viewport coordinates
  // and the panel's real rendered height - measured after it mounts, so this
  // is exact rather than a guessed threshold.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const place = () => {
      if (!buttonRef.current) return;
      const btnRect = buttonRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const left = Math.min(btnRect.left, window.innerWidth - PANEL_WIDTH - 8);
      if (panelHeight > 0 && spaceBelow < panelHeight + 8 && btnRect.top > spaceBelow) {
        setCoords({ bottom: window.innerHeight - btnRect.top + 6, left });
      } else {
        setCoords({ top: btnRect.bottom + 6, left });
      }
    };
    place();
    // Re-measure once the panel has actually rendered its full content (the
    // first layout pass above may run before it has real height).
    const raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [open, query, options.length]);

  // Close on outside click, scroll of any ancestor, or viewport resize - a
  // portal-rendered popover can't rely on a simple "did the click land
  // inside my own DOM subtree" check the way an inline one could, since the
  // button and panel are no longer in the same subtree.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Capture-phase scroll fires for scroll on any descendant too (it
    // doesn't bubble, so capture is the only way to observe it at all) -
    // scrolling the checkbox list itself must not close the panel, only
    // scrolling something outside it (the page, the table wrapper) should.
    const handleScrollOrResize = (e: Event) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  const filteredOptions = searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const clear = () => {
    setDraft([]);
    onChange([]);
    setOpen(false);
  };

  return (
    <span className="wf-colfilter">
      <button
        ref={buttonRef}
        type="button"
        className={`wf-colfilter-btn${selected.length > 0 ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        &#9660;{selected.length > 0 && <span className="wf-colfilter-count">{selected.length}</span>}
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="wf-colfilter-panel-portal"
          style={{
            left: coords?.left ?? -9999,
            top: coords?.top,
            bottom: coords?.bottom,
            visibility: coords ? 'visible' : 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {searchable && (
            <input
              type="text"
              className="wf-colfilter-search"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          <div className="wf-colfilter-list">
            {filteredOptions.length === 0 ? (
              <div className="wf-picker-empty">No values</div>
            ) : (
              filteredOptions.map((opt) => (
                <label key={opt.value} className="wf-colfilter-option">
                  <input
                    type="checkbox"
                    checked={draft.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>
          <div className="wf-colfilter-actions">
            <button type="button" className="wf-btn" onClick={clear}>Clear</button>
            <button type="button" className="wf-btn wf-btn-primary" onClick={apply}>Apply</button>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
};

export default ColumnFilterDropdown;
